import _ from 'lodash'
import EventEmitter from 'events'
import * as d3 from 'd3-selection'
import i18n from 'scoreboard/components/i18n'
import icon from 'scoreboard/components/icon'
import sortIconHeaderTemplate from './sort-icon-header.hbs'
import arrayToHash from 'scoreboard/components/array-to-hash'
import { truncateLongString } from 'scoreboard/reports/report-constants'

const TRANSIENT_COLUMN_CLASS = 'transient-column'
const NO_DRAG_CLASS = 'no-drag'
const HIDE_ON_VIEW_CLASS = 'hide-on-view'
const NO_SELECT_CLASS = 'no-select'
import { getKeyForAggregationTypeAndDataType } from 'scoreboard/datasets/overview/aggregation-label'
import reportClientId from '../model/designer-client-id'
import Dataset from 'scoreboard/datasets/model/dataset'
const { TOTAL } = Dataset
import {LAST_STACK_ADDABLE_CLASS} from './table'

/**
 * Represents a single cell within the stack header of a table
 */
class HeaderCell extends EventEmitter {

	constructor(tableStack, columnAttributes, opts = {}) {
		super()
		this.options = opts
		this._columnAttributes = columnAttributes

		this.reportModel = opts.reportModel
		this.stack = opts.stack //reference to report stack model
		this.column = columnAttributes.column //reference to report stack's column model
		this.tableStack = tableStack	//reference back to the table's stack model
		this.cid = this.column ? this.column.columnClientId() : columnAttributes.cid
		//id on the server if any
		this.sid = columnAttributes.id
		//tracks the position of the cell within a stack, used when needing to
		//attach tooltip to a specific cell position (like editing a stack)
		this.cellIndex = opts.cellIndex || 0
		this.stackAggregated = columnAttributes.stackAggregated

		let props = ['datasetFieldId', 'columnType', 'calculated',
			'sortType', 'colspan', 'rowspan',
			'datasetFieldId', 'visibleLinkedFieldId']
		_.extend(this, _.pick(columnAttributes, props))

		this._style = _.assign({}, _.result(columnAttributes, 'style', {}))
		this.style(TRANSIENT_COLUMN_CLASS, columnAttributes.transient)
		if (columnAttributes.justified) {
			this.style(`justify-${columnAttributes.justified}`, columnAttributes.justified)
		}
		//https://github.com/SpiderStrategies/Scoreboard/issues/27402#issuecomment-847308523
		if (!_.isNil(columnAttributes.groupingId)) {
			this.style('group-header-cell', true)
		}

		//assign the value of each of these properties as the classes to indicate
		//the style for the given property
		_.each(['colorStyle', 'colorType', 'fontSize'], prop => {
			if (columnAttributes[prop]) {
				this.style(columnAttributes[prop], true)
			}
		})

		_.each(['bold', 'italic', 'underlined'], prop => {
			this.style(prop, !!columnAttributes[prop])
		})

		//setup content of cell
		if (!_.isNil(columnAttributes.html)) {
			this.html = columnAttributes.html
		} else if (columnAttributes.newBlankColumn) {
			this.html = i18n.get('report.common.newColumn')
		} else {
			let label = i18n.get(columnAttributes.label)
			let useDefaultLabel = !columnAttributes.customLabel
			if (useDefaultLabel) {
				label = getDefaultLabel(this, columnAttributes, label) ?? label
			}

			this.html = label
		}


		//determine sortability
		this.sortable = false
		if (this.reportModel) {
			this.sortable = this.column && !_.get(this.reportModel
				.columnDataByFieldIdOrColumnType(this.column), 'unsortable', false)
		}
		this.style('is-sortable', this.sortable)
		let sortDirection = this._sortDirection(this.column, this.reportModel)
		if (!_.isNil(sortDirection)) {
			//#43498 nate said "there is some old logic in the parser that adds new lines for nested divs."
			//the nested divs created here are from the sort, so capture the non-sorted
			//label for the export
			this.exportModel = { value: this.html }

			this.style('is-sorted', true)
			let cssClass = sortDirection == 'ascending' ? 'is-flipped' : ''
			let html = this.html || ''
			let sortIcon = icon('collapsed', cssClass)
			if (sortDirection == 'tree') {
				sortIcon = icon('tree-order')
			}
			this.html = html+sortIconHeaderTemplate({
				content: this.label,
				sortIcon
			})
		}

		let reportEditable = _.get(this.reportModel, 'editing', false)

		//if not specified, the default states for these are true only if it's not transient
		let defaultEditable = !columnAttributes.transient && reportEditable
		this.editable = _.get(opts, 'editable', defaultEditable)
		this.transient = columnAttributes.transient || false
		this.addable = _.get(opts, 'addable', reportEditable)

		//if this is not moveable, prevent dnd
		let immovable = _.get(opts, 'immovable', columnAttributes.transient || !this.editable)
		this.style(NO_DRAG_CLASS, immovable)

		//apply not selectable if defined, or if not editing or if transient
		this.style(NO_SELECT_CLASS, _.get(opts, 'selectable', !columnAttributes.transient) == false)

		//add feature to show tooltip on hover
		this.style('hover-tooltip', true)

		this._domNodes = []
	}

	/**
	 * Return the identifier for this cell, used to bind data supplied to the
	 * d3 table
	 */
	id() {
		return `${this.cid}`
	}

	/**
	 * Returns ascending/descending/tree or null if ascending/descending/tree or not sorted (default order)
	 */
	_sortDirection(columnModel, reportModel) {
		// #46886 in the specific scenario that there is no sorting on the
		// report and there is a grouping, we are going to sort on the first
		// grouped column
		const useDefaultSort = !_.isNil(reportModel) && 
			!_.some(reportModel.allColumns(), c =>
				!_.isNil(c.get('sortType')) || !_.isNil(c.get('groupingSortType')))
		if (useDefaultSort) {
			const firstGroupingId = _.first(reportModel.get('groupBy'))
			const columnGroupingId = columnModel.get('groupingId')
			if (!_.isNil(firstGroupingId) && !_.isNil(columnGroupingId)
				&& columnGroupingId == firstGroupingId) return 'ascending'
		}

		if (!this.sortable || !columnModel.isSorting(reportModel)) {
			return null
		}

		//add the sort icon
		return columnModel.columnSortType(reportModel)
	}

	domNodes() {
		return this._domNodes
	}

	addDomNode(node) {
		d3.select(node).on('click',(e) => {
			if (node.classList.contains(NO_SELECT_CLASS)) { return }

			//select the current nodes first
			this.emit('select', this, this.domNodes())

			//code responding to the select may have wiped out the current node
			//only continue if the node is still connected to the DOM
			if (node.isConnected) {
				this.tableStack.select(true, this, node)
				//then emit the edit, since that could cause the table to change
				//and a reselction of new nodes
				if (this.editable) {
					this.emit('edit', this, this.domNodes(), node, e)
				}
			}
			e.stopPropagation()
		})
		this._domNodes.push(node)
	}

	resetDomNodes() {
		this._domNodes = []
	}

	style(property, enabled) {
		if (arguments.length == 0) {
			return this._style
		}
		if (_.isObject(property)) {
			let prevStyle =  this._style
			this._style = property
			return prevStyle
		}
		let prevValue = this._style[property]
		this._style[property] = enabled
		return prevValue
	}

	//the start and end are called by the stack being dragged
	dragStart() {
		this.tableStack.drag(this, true)
		this.emit('drag')
	}

	dragEnd(endZone) {
		this.tableStack.drag(this, false)
		if (!endZone) { return }

		endZone.headerCell.tableStack.clearDropLines()
		if (this.tableStack.allowDrop(this, endZone.headerCell, endZone)) {
			this.emit('move', this, endZone.headerCell, endZone)
		}
	}

	//the end/exit are called by the stack being targeted
	dragEnter(dragCell, zone, adjacentZones) {
		let dragStack = dragCell.tableStack
		if (!dragStack.allowDrop(dragCell, this, zone)) {
			return
		}
		this.tableStack.accept({ dragCell, dropCell:this, zone, adjacentZones, enter: true})
	}

	dragExit(dragCell, zone, adjacentZones) {
		this.tableStack.accept({ dragCell, dropCell:this, zone, adjacentZones, enter: false})
	}

	rowIndex(index) {
		if (_.isNil(index)) {
			return this._rowIndex
		} else {
			this._rowIndex = index
		}
	}

	/**
	 * Returns true if we're allowed to repeat by in this header cell
	 */
	allowRepeatBy() {
		let {columnType, groupingId} = _.get(this.column, 'attributes', {})
		let enabledGroupingIds = arrayToHash(this.reportModel.get('groupBy') || [])
		//cannot repeat by on the stack that is grouped
		if (groupingId && enabledGroupingIds[groupingId]) {
			return false
		}

		//cannot repeat by on notes column
		if (columnType == 'notes') {
			return false
		}

		let isReportNotGrouping = _.isEmpty(enabledGroupingIds)
		if (isReportNotGrouping) {
			if (!this._allowUngroupedPeriodRangeRepeatBy(columnType)) {
				return false
			}
		} else if (this.reportModel.isShowingIndividualRecords()) {
			//if grouping but showing individual records you cannot repeat by
			return false
		}

		//passed all the conditions preventing repeat by, so allow
		return true
	}

	_allowUngroupedPeriodRangeRepeatBy(columnType) {
		//only initiative reports allow repeat bys in a ungrouped scenarios and
		//the column type must allow it
		let {changesOverTime} = this.reportModel.columnDataByFieldIdOrColumnType(columnType)
		return this.reportModel.reportDomainType === 'initiative' && changesOverTime
	}

	/**
	 * Returns a hash of style properties set by the column attributes of this
	 * header cell
	 */
	columnStyle() {
		let style = _.clone(this.style())
		//don't cascade this down
		delete style[HIDE_ON_VIEW_CLASS]
		return style
	}

	addIndex(addMarker) {
		if (addMarker.classList.contains(LAST_STACK_ADDABLE_CLASS) ||
			addMarker.classList.contains('addable-right')) {
			//else check to see if we're adding on the last most marker or if the
			// the button is on the right side of the column, if so add to the right
			// of the index
			return this.tableStack.index + 1
		}
		return this.tableStack.index
	}

	onHover(evt) {
		if (!this.editable && this.sortable && this.reportModel.showSortWarning) {
			return i18n.get('report.designer.onlySortSaveOnEdit')
		}
		//if nothing to show return false to prevent the tooltip
		return false
	}

	breakdownDataKey() {
		if (!this.breakdownColumnData) {
			return ''
		}
		let stackClientId = reportClientId(this.stack)
		let columnClientId = reportClientId(this.column)
		let {firstBreakdownItemId} = this.breakdownColumnData.breakdownItemId
		let secondBreakdownItemId = this.breakdownColumnData.breakdownItemId.secondBreakdownItemId || ''
		return `${stackClientId}${columnClientId}${firstBreakdownItemId}${secondBreakdownItemId}`
	}

}

const getDefaultLabel = (cell, columnAttributes, label) => {
	if (cell.stackAggregated) {
		//if we're aggregating, and using the default label, then we
		//need use to the aggregation version of the default label
		let aggType = cell.column.get('aggregationType') ||
			cell.column.defaultColumnData(cell.reportModel)?.defaultAggregationType

		if (aggType == 'use-equation' || !!columnAttributes.calculated) {
			return truncateLongString(cell.column.get('equation'))
		} else {
			if (aggType && columnAttributes.datasetFieldId !== TOTAL) {
				const key = getKeyForAggregationTypeAndDataType(aggType,
					columnAttributes.dataType)
				if (cell.column.get('calculated')) {
					return cell.column.get('equation')
				} else {
					return i18n.get(key, label)
				}
			}
		}
	} else if (columnAttributes.calculated) {
		// #38703 Use equation if column is calculated but not named
		return truncateLongString(columnAttributes.column.get('equation'))
	}
}

export default HeaderCell
export { TRANSIENT_COLUMN_CLASS }
export { NO_DRAG_CLASS }
export { HIDE_ON_VIEW_CLASS }
export { NO_SELECT_CLASS }
export { getDefaultLabel }

HeaderCell.__filename__ = 'reports/common/table/header-cell.js'
